/**
 * Renderer registry.
 *
 * An ordered list of {@link FileRenderer} entries. {@link pickRenderer}
 * returns the first one whose `matches(source)` returns true after
 * stable-sorting by descending `priority`. The list ends with the
 * unconditional `UnsupportedRenderer` so dispatch never returns null.
 *
 * Both the source's declared `kind` and (failing that) the inferred kind
 * from `inferFileKind(filename + contentType)` are consulted, so callers
 * can either pre-classify (artifacts, tool outputs) or let the registry
 * infer (raw URL attachments).
 */
import type { FileRenderer, FileSource } from "../types";
import { inferFileKind } from "../utils/file-type";

import { AudioRenderer } from "./audio-renderer";
import { CodeSourceRenderer } from "./code-renderer";
import { CsvSourceRenderer } from "./csv-renderer";
import { DocumentSourceRenderer } from "./document-renderer";
import { DocxRenderer } from "./docx-renderer";
import { HtmlRenderer } from "./html-renderer";
import { ImageRenderer } from "./image-renderer";
import { MarkdownSourceRenderer } from "./markdown-renderer";
import { PdfRenderer } from "./pdf-renderer";
import { PresentationRenderer } from "./presentation-renderer";
import { SpreadsheetRenderer } from "./spreadsheet-renderer";
import { UnsupportedRenderer } from "./unsupported-renderer";
import { VideoRenderer } from "./video-renderer";

/**
 * Effective kind of a source — caller-declared wins over inference.
 * Hoisted into a helper so every matcher uses the same rule.
 */
function effectiveKind(source: FileSource) {
  return (
    source.kind ??
    inferFileKind({ filename: source.filename, contentType: source.contentType })
  );
}

/**
 * Registry entries are matched in descending `priority` order. Specific
 * MIMEs / kinds (pdf, html, presentation) sit at 100; the catch-all
 * unsupported fallback sits at 0.
 *
 * Direct mutation of this array is not supported — if a future caller
 * needs to inject extra renderers, expose a per-call `renderers` prop on
 * the dialog instead.
 */
export const fileRenderers: FileRenderer[] = [
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "pdf",
    Component: PdfRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "image",
    Component: ImageRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "html",
    Component: HtmlRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "spreadsheet",
    Component: SpreadsheetRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "presentation",
    Component: PresentationRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "audio",
    Component: AudioRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "video",
    Component: VideoRenderer,
  },
  {
    priority: 100,
    matches: (s) => effectiveKind(s) === "docx",
    Component: DocxRenderer,
  },
  {
    priority: 60,
    matches: (s) => effectiveKind(s) === "document",
    Component: DocumentSourceRenderer,
  },
  {
    priority: 50,
    matches: (s) => effectiveKind(s) === "markdown",
    Component: MarkdownSourceRenderer,
  },
  {
    priority: 50,
    matches: (s) => effectiveKind(s) === "csv",
    Component: CsvSourceRenderer,
  },
  {
    priority: 50,
    matches: (s) => effectiveKind(s) === "code" || effectiveKind(s) === "text",
    Component: CodeSourceRenderer,
  },
  {
    priority: 0,
    matches: () => true,
    Component: UnsupportedRenderer,
  },
];

/**
 * Sort the registry once at module load — the renderer list is static, so
 * paying the sort cost on every {@link pickRenderer} call would burn cycles
 * for no benefit. {@link Array.prototype.sort} is stable in modern engines,
 * so registry entries with the same `priority` keep their declared order.
 */
const sortedRenderers = [...fileRenderers].sort(
  (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
);

/**
 * Pick the highest-priority renderer that claims `source`. Falls back to
 * the last registry entry (the unconditional `UnsupportedRenderer`) when
 * — somehow — nothing matched; the cast is the TypeScript-friendly way
 * to assert that.
 */
export function pickRenderer(source: FileSource): FileRenderer {
  const hit = sortedRenderers.find((r) => r.matches(source));
  if (!hit) {
    return sortedRenderers[sortedRenderers.length - 1]!;
  }
  return hit;
}
